// src/GroomerRebooks.js
import React, { useState } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";

const GroomerRebooks = () => {
  const [monthYear, setMonthYear] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);

  // Get the current month and year for default value
  const getCurrentMonthYear = () => {
    const now = new Date();
    // Format as YYYY-MM
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // Month names for the report title
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Format month-year for display
  const formatMonthYear = (monthYearStr) => {
    if (!monthYearStr) return "";
    
    const [year, month] = monthYearStr.split('-');
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const handleRunReport = () => {
    if (!monthYear) {
      setError("Please select a month and year.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Call the API endpoint
    apiFetch(`/api/reports/groomer-rebooks?month_year=${monthYear}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => { 
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error running report:", err);
        setError("Error running report: " + err.message);
        setLoading(false);
      });
  };

    // Group data by groomer with distinct appointment counts
    const getGroomerSummary = () => {
      if (!reportData || reportData.length === 0) return [];
      
      const groomerMap = {};
      const processedAppointments = new Set(); // Track unique appointment IDs
      
      reportData.forEach(record => {
        // Create groomer entry if it doesn't exist
        if (!groomerMap[record.createdBy]) {
          groomerMap[record.createdBy] = {
            groomerName: record.createdBy,
            appointments: 0,
            pets: 0,
            appointmentDetails: []
          };
        }
        
        // Only count each unique appointment once
        const appointmentKey = record.bookingId;
        if (!processedAppointments.has(appointmentKey)) {
          groomerMap[record.createdBy].appointments++;
          groomerMap[record.createdBy].pets += record.numPets;
          processedAppointments.add(appointmentKey);
        }
        
        groomerMap[record.createdBy].appointmentDetails.push(record);
      });
    
    return Object.values(groomerMap).sort((a, b) => 
      a.groomerName.localeCompare(b.groomerName)
    );
  };

  const groomerSummary = getGroomerSummary();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Groomer Rebooks Report
      </Typography>
      
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mb: 2 }}
      >
        <TextField
          label="Month and Year"
          type="month"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={monthYear}
          onChange={(e) => setMonthYear(e.target.value)}
          placeholder={getCurrentMonthYear()}
        />
        
        <Button 
          variant="contained" 
          onClick={handleRunReport}
          disabled={loading}
        >
          {loading ? "Loading..." : "Run Report"}
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Report header with title and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Groomer Rebooks Report - {formatMonthYear(monthYear)}</Typography>
          <Typography variant="caption">
            Report generated: {reportGeneratedAt.toLocaleString()}
          </Typography>
        </Box>
      )}
      
      {/* Report results display */}
      {reportData && reportData.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {/* Summary table by groomer */}
          <Typography variant="h6" gutterBottom>Summary by Groomer</Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Groomer</TableCell>
                  <TableCell align="right">Appointments</TableCell>
                  <TableCell align="right">Pets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groomerSummary.map((groomer, idx) => (
                  <TableRow key={idx}>
                    <TableCell component="th" scope="row">{groomer.groomerName}</TableCell>
                    <TableCell align="right">{groomer.appointments}</TableCell>
                    <TableCell align="right">{groomer.pets}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell component="th" scope="row"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{groomerSummary.reduce((sum, groomer) => sum + groomer.appointments, 0)}</strong></TableCell>
                  <TableCell align="right"><strong>{groomerSummary.reduce((sum, groomer) => sum + groomer.pets, 0)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Detailed appointments table */}
          <Typography variant="h6" gutterBottom>Detailed Appointments</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Groomer</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Pet</TableCell>
                  <TableCell>Creation Date</TableCell>
                  <TableCell>Appointment Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((record, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{record.createdBy}</TableCell>
                    <TableCell>{record.customerName}</TableCell>
                    <TableCell>{record.petNames}</TableCell>
                    <TableCell>{record.creationDate}</TableCell>
                    <TableCell>{record.apptDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      ) : reportGeneratedAt && (
        <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
          No rebook data found for the selected month.
        </Typography>
      )}
    </Box>
  );
};

export default GroomerRebooks;